<template>
    <span class="logo-gato" :class="claseEstado">
        <svg class="logo-svg" viewBox="0 0 361.53 338.31"
            xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <!-- Capa coral (06) -->
            <path fill="#e6342e" opacity=".92"
                d="M143.92,125.5c-47.42,0-85.87,38.44-85.87,85.87,0,8.58,1.27,16.86,3.62,24.67,8.12-12.38,22.12-20.56,38.03-20.56,25.11,0,45.47,20.36,45.47,45.47,0,14.04-6.37,26.6-16.38,34.94,4.91.87,9.96,1.36,15.13,1.36,47.42,0,85.87-38.44,85.87-85.87s-38.44-85.87-85.87-85.87Z" />
            <!-- Capa M / cara del gato (04) -->
            <path fill="#233976"
                d="M179.81,193.83l-82.84-105.43c-4.08-5.19-12.42-2.31-12.42,4.3v125.39c5.48-1.94,11.44-2.87,17.65-2.53,18.23.98,34.12,13.37,40.34,30.53,2.7,7.46,3.12,14.63,2.16,21.36h126.04V89.71c0-6.74-8.63-9.54-12.58-4.08l-78.35,108.19Z" />
            <!-- Capa perifollo (05): gira como spinner en estado "pensando" -->
            <g class="capa-peri">
                <path fill="#4965ad" opacity=".49"
                    d="M315.56,91.94c1.31,5.89,2,12.02,2,18.3,0,46.37-37.59,83.96-83.96,83.96s-83.96-37.59-83.96-83.96S187.23,26.28,233.6,26.28c40.08,0,73.61,28.09,81.96,65.66" />
            </g>
            <!-- Punto azul (07): pulsa como indicador de voz en "narrando" -->
            <circle class="punto-voz" fill="#233976" cx="104.55" cy="251.77" r="23.82" />
            <!-- Ojos de gato -->
            <g class="ojo">
                <ellipse cx="150" cy="231" rx="15" ry="16.5" fill="#ffffff" />
                <ellipse class="pupila" cx="150" cy="231" rx="6.2" ry="10.5" fill="#233976" />
                <circle cx="153" cy="226" r="2.6" fill="#ffffff" opacity="0.9" />
            </g>
            <g class="ojo">
                <ellipse cx="222" cy="231" rx="15" ry="16.5" fill="#ffffff" />
                <ellipse class="pupila" cx="222" cy="231" rx="6.2" ry="10.5" fill="#233976" />
                <circle cx="225" cy="226" r="2.6" fill="#ffffff" opacity="0.9" />
            </g>
        </svg>
    </span>
</template>

<script>
// Cursor compartido entre TODAS las instancias del logo: un solo listener
// global con refcount, sin importar cuántos gatos haya en pantalla.
const cursor = { x: 0, y: 0, listo: false };
let suscriptores = 0;

function registrarCursor(evento) {
    cursor.x = evento.clientX;
    cursor.y = evento.clientY;
    cursor.listo = true;
}

function suscribirCursor() {
    suscriptores += 1;
    if (suscriptores === 1) window.addEventListener("pointermove", registrarCursor);
}

function desuscribirCursor() {
    suscriptores -= 1;
    if (suscriptores === 0) window.removeEventListener("pointermove", registrarCursor);
}

export default {
    name: "RiskMannLogoGato",

    props: {
        // reposo | pensando | narrando
        estado: { type: String, default: "reposo" },
        // Pupilas siguiendo el cursor + parpadeo aleatorio
        interactivo: { type: Boolean, default: false },
        // Inclinación sutil hacia el cursor (solo el FAB)
        magnetico: { type: Boolean, default: false },
        // Punto {x, y} en viewport hacia donde mirar (prioridad sobre el cursor);
        // mientras esté fijado, el lean magnético descansa
        objetivo: { type: Object, default: null },
        // Reacciona al scroll de la página: mira hacia donde se desplaza y el
        // perifollo gira como una rueda (solo el FAB)
        reactivoScroll: { type: Boolean, default: false },
        // Contador: cada incremento hace saltar al gato (flecha de volver arriba)
        saltos: { type: Number, default: 0 },
    },

    data() {
        return { derivaT: 0, saltando: false };
    },

    computed: {
        claseEstado() {
            return {
                "st-pensando": this.estado === "pensando",
                "st-narrando": this.estado === "narrando",
                "st-saltando": this.saltando,
            };
        },
    },

    watch: {
        saltos() {
            this.saltar();
        },
    },

    mounted() {
        const reducirMovimiento =
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (!this.interactivo || reducirMovimiento) return;
        suscribirCursor();
        this._suscrito = true;
        if (this.reactivoScroll) {
            this._scrollAnterior = window.scrollY;
            window.addEventListener("scroll", this.alDesplazar, { passive: true });
        }
        this.ciclo();
        this.programarParpadeo();
    },

    beforeDestroy() {
        if (this._rafId) cancelAnimationFrame(this._rafId);
        if (this._timerParpadeo) clearTimeout(this._timerParpadeo);
        if (this._timerCierre) clearTimeout(this._timerCierre);
        if (this._timerSalto) clearTimeout(this._timerSalto);
        if (this._suscrito) desuscribirCursor();
        window.removeEventListener("scroll", this.alDesplazar);
    },

    methods: {
        ciclo() {
            this.derivaT += 0.03;
            this.actualizarPupilas();
            if (this.magnetico) this.actualizarLean();
            if (this.reactivoScroll) this.actualizarPerifollo();
            this._rafId = requestAnimationFrame(this.ciclo);
        },

        // ── Reacción al scroll y salto (solo el FAB) ─────────────────────
        alDesplazar() {
            const actual = window.scrollY;
            const delta = actual - this._scrollAnterior;
            this._scrollAnterior = actual;
            if (!delta) return;
            this._direccionScroll = Math.sign(delta);
            this._scrollHasta = performance.now() + 250;
            // El perifollo gira con el desplazamiento, como una rueda lenta
            this._giroObjetivo = (this._giroObjetivo || 0) + delta * 0.3;
        },

        actualizarPerifollo() {
            // En "pensando" manda la animación de spinner
            if (this.estado === "pensando") return;
            if (!this._perifollo) this._perifollo = this.$el.querySelector(".capa-peri");
            if (!this._perifollo) return;
            // Se acerca al giro acumulado poco a poco: se ve continuo aunque
            // los eventos de scroll lleguen a saltos
            const objetivo = this._giroObjetivo || 0;
            this._giro = (this._giro || 0) + (objetivo - (this._giro || 0)) * 0.15;
            this._perifollo.style.transform = `rotate(${this._giro}deg)`;
        },

        saltar() {
            if (this._timerSalto) clearTimeout(this._timerSalto);
            // Se quita y se vuelve a poner en el siguiente cuadro: así la
            // animación se reinicia si se pulsa de nuevo antes de terminar
            this.saltando = false;
            requestAnimationFrame(() => {
                this.saltando = true;
                this._timerSalto = setTimeout(() => {
                    this.saltando = false;
                }, 850);
            });
        },

        actualizarPupilas() {
            const pupilas = this.$el.querySelectorAll(".pupila");
            pupilas.forEach((pupila) => {
                const caja = pupila.getBoundingClientRect();
                if (!caja.width) return; // oculto: no hay nada que animar
                const cx = caja.left + caja.width / 2;
                const cy = caja.top + caja.height / 2;
                let dx = 0;
                let dy = 0;
                if (this.saltando) {
                    // Salto de "volver arriba": mira hacia donde va la página
                    dy = -6.5;
                } else if (this.objetivo) {
                    // Mirada dirigida (ej. hacia la burbuja de sugerencias)
                    const ang = Math.atan2(this.objetivo.y - cy, this.objetivo.x - cx);
                    const dist = Math.min(6.5, Math.hypot(this.objetivo.x - cx, this.objetivo.y - cy) / 40);
                    dx = Math.cos(ang) * dist;
                    dy = Math.sin(ang) * dist;
                } else if (this.estado === "pensando") {
                    // Deriva pensativa: mira "al techo" y oscila
                    dx = Math.sin(this.derivaT * 1.6) * 4;
                    dy = -6 + Math.sin(this.derivaT * 0.9) * 1.5;
                } else if (this.reactivoScroll && this._scrollHasta && performance.now() < this._scrollHasta) {
                    // Mira hasta el borde del ojo hacia donde se desplaza la página
                    dy = this._direccionScroll * 6.5;
                } else if (cursor.listo) {
                    const ang = Math.atan2(cursor.y - cy, cursor.x - cx);
                    const dist = Math.min(6.5, Math.hypot(cursor.x - cx, cursor.y - cy) / 40);
                    dx = Math.cos(ang) * dist;
                    dy = Math.sin(ang) * dist;
                }
                pupila.style.transform = `translate(${dx}px, ${dy}px)`;
            });
        },

        actualizarLean() {
            // Con la mirada fijada en un objetivo (cursor encima del FAB),
            // el logo descansa quieto: sin lean que tiemble bajo el cursor
            if (this.objetivo) {
                this.$el.style.transform = "";
                return;
            }
            const caja = this.$el.getBoundingClientRect();
            if (!caja.width || !cursor.listo) return;
            const cx = caja.left + caja.width / 2;
            const cy = caja.top + caja.height / 2;
            const dx = cursor.x - cx;
            const dy = cursor.y - cy;
            const dist = Math.hypot(dx, dy) || 1;
            // Se inclina más cuando el cursor está cerca, pero la inclinación
            // se amortigua a 0 al llegar al centro: si no, la dirección se
            // invierte a cada frame bajo el cursor y el logo tiembla
            const mag = Math.min(7, 900 / dist) * Math.min(1, dist / 130);
            const rot = (dx / dist) * 6 * Math.min(1, dist / 130);
            this.$el.style.transform =
                `translate(${(dx / dist) * mag}px, ${(dy / dist) * mag}px) rotate(${rot}deg)`;
        },

        programarParpadeo() {
            const espera = 2500 + Math.random() * 3000;
            this._timerParpadeo = setTimeout(() => {
                const ojos = this.$el.querySelectorAll(".ojo");
                ojos.forEach((ojo) => ojo.classList.add("parpadeo"));
                this._timerCierre = setTimeout(() => {
                    ojos.forEach((ojo) => ojo.classList.remove("parpadeo"));
                }, 150);
                this.programarParpadeo();
            }, espera);
        },
    },
};
</script>

<style scoped>
.logo-gato {
    display: block;
    width: 100%;
    height: 100%;
    /* Suaviza el lean magnético frame a frame (sin esto el movimiento vibra) */
    transition: transform 0.15s ease-out;
}

@media (prefers-reduced-motion: reduce) {
    .logo-gato {
        transition: none;
    }
}

.logo-svg {
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible;
}

.capa-peri {
    transform-box: view-box;
    /* Centro del arco perifollo: sobre él rota el spinner */
    transform-origin: 233.6px 110.24px;
}

.punto-voz {
    transform-box: fill-box;
    transform-origin: center;
}

.ojo {
    transform-box: fill-box;
    transform-origin: center;
    transition: transform 0.12s ease;
}

.ojo.parpadeo {
    transform: scaleY(0.08);
}

.pupila {
    transition: transform 0.08s linear;
}

/* Estado: pensando — el arco perifollo gira como spinner */
.st-pensando .capa-peri {
    animation: giro-peri 1.3s linear infinite;
}

@keyframes giro-peri {
    to {
        transform: rotate(360deg);
    }
}

/* Estado: narrando — el punto pulsa como indicador de voz,
   el logo hace un bob sutil y los ojos se entrecierran felices */
.st-narrando .punto-voz {
    animation: pulso-voz 0.55s ease-in-out infinite;
}

@keyframes pulso-voz {
    50% {
        transform: scale(1.4);
    }
}

.st-narrando .logo-svg {
    animation: bob-logo 0.9s ease-in-out infinite;
}

@keyframes bob-logo {
    50% {
        transform: translateY(-3px);
    }
}

.st-narrando .ojo {
    transform: scaleY(0.62);
}

/* Flecha de volver arriba: el gato toma impulso, salta y aterriza. Va sobre el
   SVG y no sobre .logo-gato, que usa transform para el lean magnético. */
.st-saltando .logo-svg {
    transform-origin: 50% 100%;
    animation: salto-gato 0.8s ease-in-out;
}

@keyframes salto-gato {
    0% {
        transform: translateY(0) scale(1, 1);
    }

    15% {
        transform: translateY(1px) scale(1.04, 0.94);
    }

    45% {
        transform: translateY(-10px) scale(0.97, 1.04);
    }

    70% {
        transform: translateY(0) scale(1.03, 0.97);
    }

    85% {
        transform: translateY(-2px) scale(0.99, 1.01);
    }

    100% {
        transform: translateY(0) scale(1, 1);
    }
}

@media (prefers-reduced-motion: reduce) {

    .st-pensando .capa-peri,
    .st-narrando .punto-voz,
    .st-narrando .logo-svg,
    .st-saltando .logo-svg {
        animation: none;
    }
}
</style>
